import { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import type { IconLike } from '../ui/KyraIcon';

export type QuickNavigationItem = {
  id?: string;
  to: string;
  label: string;
  moduleLabel: string;
  sectionLabel: string;
  icon: IconLike;
  kind?: 'navigation' | 'action';
  description?: string;
  keywords?: string[];
  state?: Record<string, unknown>;
};

type Props = {
  items: QuickNavigationItem[];
  onNavigate: (item: QuickNavigationItem) => void;
};

const normalizeSearch = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('es')
    .trim();

const scoreItem = (item: QuickNavigationItem, normalizedQuery: string) => {
  const label = normalizeSearch(item.label);
  const keywords = normalizeSearch((item.keywords ?? []).join(' '));
  const context = normalizeSearch(
    `${item.moduleLabel} ${item.sectionLabel} ${item.description ?? ''}`,
  );
  const searchable = `${label} ${keywords} ${context}`;
  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  if (!tokens.every((token) => searchable.includes(token))) return -1;

  let score = item.kind === 'action' ? 8 : 0;
  if (label === normalizedQuery) score += 120;
  else if (label.startsWith(normalizedQuery)) score += 90;
  else if (label.includes(normalizedQuery)) score += 65;
  if (keywords.includes(normalizedQuery)) score += 40;
  score += tokens.filter((token) => label.includes(token)).length * 12;
  score += tokens.filter((token) => keywords.includes(token)).length * 7;
  return score;
};

export function QuickNavigationSearch({ items, onNavigate }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const results = useMemo(() => {
    const normalizedQuery = normalizeSearch(query);
    if (!normalizedQuery) return [];
    return items
      .map((item) => ({ item, score: scoreItem(item, normalizedQuery) }))
      .filter((entry) => entry.score >= 0)
      .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label, 'es'))
      .slice(0, 8);
  }, [items, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && rootRef.current && !rootRef.current.contains(target)) {
        setOpen(false);
        setMobileExpanded(false);
      }
    };
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        setMobileExpanded(true);
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleShortcut);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleShortcut);
    };
  }, []);

  const choose = (item: QuickNavigationItem) => {
    setQuery('');
    setOpen(false);
    setMobileExpanded(false);
    onNavigate(item);
  };

  return (
    <div className={`quick-nav-search ${mobileExpanded ? 'is-mobile-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="quick-nav-mobile-trigger"
        aria-label="Abrir búsqueda rápida"
        aria-expanded={mobileExpanded}
        onClick={() => {
          setMobileExpanded(true);
          setOpen(true);
          window.requestAnimationFrame(() => inputRef.current?.focus());
        }}
      >
        <MagnifyingGlass size={20} weight="bold" />
      </button>
      <div className="quick-nav-field">
        <MagnifyingGlass className="quick-nav-search-icon" size={18} aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          placeholder="Buscar páginas o acciones…"
          aria-label="Buscar páginas o acciones del sistema"
          aria-expanded={open && Boolean(query)}
          aria-controls="quick-nav-results"
          aria-autocomplete="list"
          role="combobox"
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              setMobileExpanded(false);
              inputRef.current?.blur();
              return;
            }
            if (!results.length) return;
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActiveIndex((current) => (current + 1) % results.length);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((current) => (current - 1 + results.length) % results.length);
            } else if (event.key === 'Enter') {
              event.preventDefault();
              choose(results[activeIndex]?.item ?? results[0].item);
            }
          }}
        />
        <kbd className="quick-nav-shortcut">⌘K</kbd>
        <button
          type="button"
          className="quick-nav-mobile-close"
          aria-label="Cerrar búsqueda rápida"
          onClick={() => {
            setOpen(false);
            setMobileExpanded(false);
            setQuery('');
          }}
        >
          <X size={18} weight="bold" />
        </button>
      </div>

      {open && query.trim() ? (
        <div id="quick-nav-results" className="quick-nav-results" role="listbox">
          {results.length ? (
            results.map(({ item }, index) => {
              const ResultIcon = item.icon;
              return (
                <button
                  key={item.id ?? `${item.moduleLabel}-${item.to}`}
                  type="button"
                  className={`quick-nav-result ${index === activeIndex ? 'is-active' : ''}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(item)}
                >
                  <span className="quick-nav-result-icon">
                    <ResultIcon size={18} weight="duotone" />
                  </span>
                  <span className="quick-nav-result-copy">
                    <span className="quick-nav-result-title">
                      <strong>{item.label}</strong>
                      <em>{item.kind === 'action' ? 'Acción' : 'Ir a'}</em>
                    </span>
                    <small>
                      {item.moduleLabel} · {item.description ?? item.sectionLabel}
                    </small>
                  </span>
                </button>
              );
            })
          ) : (
            <div className="quick-nav-empty">
              <strong>No encontramos esa opción</strong>
              <span>Prueba con otro nombre o módulo.</span>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
